import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, TrendingDown, TrendingUp, AlertTriangle, DollarSign, BarChart3, Bitcoin, Wallet, Activity, Shield, RefreshCw, Database } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import DeRiskAnalysisPanel from '../components/DeRiskAnalysisPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : `$${(n / 1e3).toFixed(0)}K`;
const RISK_COLOR = (s) => s >= 80 ? "bg-red-500" : s >= 60 ? "bg-orange-500" : "bg-yellow-500";
const RISK_TEXT = (s) => s >= 80 ? "text-red-600" : s >= 60 ? "text-orange-600" : "text-yellow-600";
const STATUS_BADGE = {
  "90+ Days Late": "bg-red-100 text-red-700",
  "Special Servicing": "bg-orange-100 text-orange-700",
  "Maturity Default": "bg-red-100 text-red-700",
  "DSCR < 1.0": "bg-yellow-100 text-yellow-700",
  "Watchlist": "bg-yellow-100 text-yellow-700",
  "Non-Performing": "bg-red-100 text-red-700",
  "Foreclosure": "bg-red-100 text-red-700",
  "REO": "bg-red-100 text-red-700",
  "60+ Days Late": "bg-orange-100 text-orange-700",
  "30+ Days Late": "bg-yellow-100 text-yellow-700",
  "Under Review": "bg-yellow-100 text-yellow-700",
  "Performing": "bg-green-100 text-green-700",
  "Current": "bg-green-100 text-green-700",
  "Active": "bg-green-100 text-green-700",
};
const TYPE_ICON = { Office: Building2, Retail: DollarSign, Multifamily: Building2, Hospitality: Building2, Industrial: Building2, office: Building2, retail: DollarSign, multifamily: Building2, hospitality: Building2, industrial: Building2, 'mixed-use': Building2 };

const Dashboard = () => {
  const navigate = useNavigate();
  const [view, setView] = useState("marketplace");
  const [selected, setSelected] = useState(null);
  const [filterType, setFilterType] = useState("All");
  const [sortBy, setSortBy] = useState("risk_score");
  const [btcCalc, setBtcCalc] = useState({ amount: 5000000, term: 24, rate: 8.5 });
  const [lenderTab, setLenderTab] = useState("portfolio");
  const [btcPrice, setBtcPrice] = useState(83420);
  const [ethPrice, setEthPrice] = useState(3200);
  const [deRiskProperty, setDeRiskProperty] = useState(null);
  const [showDeRiskPanel, setShowDeRiskPanel] = useState(false);
  
  // Real data state
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scraperStatus, setScraperStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchCryptoPrices();
    fetchProperties();
    fetchScraperStatus();
    const interval = setInterval(fetchCryptoPrices, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchCryptoPrices = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/crypto/prices`);
      const data = await response.json();
      if (data.success) {
        setBtcPrice(data.prices.BTC.price);
        setEthPrice(data.prices.ETH.price);
      }
    } catch (error) {
      console.error('Error fetching crypto prices:', error);
    }
  };

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/cre/cre-properties?limit=100`);
      const data = await response.json();
      
      if (Array.isArray(data)) {
        // Transform data to match expected format
        const transformedData = data.map(p => ({
          ...p,
          // Ensure all required fields exist with defaults
          id: p.id || `CRE-${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
          riskScore: p.risk_score || calculateRiskScore(p),
          loanBalance: p.loan_amount || 0,
          currentLTV: p.ltv_ratio || 75,
          dscr: p.dscr || calculateDSCR(p),
          credIQScore: p.cred_iq_score || calculateCredIQ(p),
          btcLendingRate: p.btc_lending_rate || 8.5,
          assumableBalance: p.assumable_balance || Math.round(p.loan_amount * 0.7),
          discount: p.discount || (p.is_distressed ? 25 : 10),
          watchlist: p.is_distressed || false,
          type: capitalizeFirst(p.property_type),
          occupancy: p.occupancy_rate,
          maturityDate: p.maturity_date || '2025-12-31',
        }));
        setProperties(transformedData);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchScraperStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/scraper/status`);
      const data = await response.json();
      if (data.success) {
        setScraperStatus(data);
      }
    } catch (error) {
      console.error('Error fetching scraper status:', error);
    }
  };

  const runScraper = async () => {
    try {
      setRefreshing(true);
      const response = await fetch(`${BACKEND_URL}/api/scraper/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count_per_market: 15 })
      });
      const data = await response.json();
      
      if (data.success) {
        await fetchProperties();
        await fetchScraperStatus();
      }
    } catch (error) {
      console.error('Error running scraper:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Helper functions for calculated fields
  const calculateRiskScore = (p) => {
    const dscr = p.dscr || 0.8;
    const ltv = p.ltv_ratio || 75;
    const occ = p.occupancy_rate || 70;
    return Math.min(100, Math.max(20, Math.round(100 - (dscr * 30) - ((100 - ltv) * 0.3) - (occ * 0.2) + (p.is_distressed ? 30 : 0))));
  };

  const calculateDSCR = (p) => {
    if (p.noi && p.loan_amount) {
      const debtService = p.loan_amount * 0.065;
      return Math.round((p.noi / debtService) * 100) / 100;
    }
    return 0.85;
  };

  const calculateCredIQ = (p) => {
    const dscr = p.dscr || 0.85;
    const ltv = p.ltv_ratio || 75;
    if (dscr >= 1.25 && ltv <= 70) return 'B+';
    if (dscr >= 1.0 && ltv <= 80) return 'C+';
    if (dscr >= 0.75) return 'C-';
    return 'D';
  };

  const capitalizeFirst = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

  const fmtBtc = (usd) => `₿${(usd / btcPrice).toFixed(2)}`;

  const filtered = properties
    .filter((p) => filterType === "All" || p.type?.toLowerCase() === filterType.toLowerCase() || p.property_type?.toLowerCase() === filterType.toLowerCase())
    .sort((a, b) => (b[sortBy] || b.risk_score || 0) - (a[sortBy] || a.risk_score || 0));

  const LOAN_TOTAL = properties.reduce((a, p) => a + (p.loanBalance || p.loan_amount || 0), 0);
  const AVG_DSCR = properties.length > 0 ? (properties.reduce((a, p) => a + (p.dscr || 0.8), 0) / properties.length).toFixed(2) : '0.00';

  const btcMonthly = ((btcCalc.amount * (btcCalc.rate / 100 / 12)) / (1 - Math.pow(1 + btcCalc.rate / 100 / 12, -btcCalc.term))).toFixed(0);
  const btcTotal = (btcMonthly * btcCalc.term).toFixed(0);

  return (
    <div className="min-h-screen bg-[#F7F7F7]">
      <Navbar />
      
      <div className="pt-20 pb-12">
        {/* Live Ticker */}
        <div className="bg-white border-b border-gray-200 py-2 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 flex items-center justify-between text-sm">
            <div className="flex items-center space-x-6">
              <span className="flex items-center text-[#006AFF] font-semibold">
                <Activity className="h-4 w-4 mr-1" />
                LIVE FEED
              </span>
              <span className="text-[#2A2A33]">
                <span className="text-[#6B6B76]">BTC/USD</span> ${btcPrice.toLocaleString()} <span className="text-green-600">+2.3%</span>
              </span>
              <span className="text-[#2A2A33]">
                <span className="text-[#6B6B76]">ETH/USD</span> ${ethPrice.toLocaleString()} <span className="text-green-600">+1.8%</span>
              </span>
            </div>
            <div className="hidden md:flex items-center space-x-6 text-[#6B6B76]">
              <span>CMBS Spreads +145bps</span>
              <span>Office Vacancy 18.4%</span>
              <span>Cred-IQ Active: 847 Loans</span>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 pt-6">
          {/* Dashboard Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#2A2A33]">Dashboard</h1>
              <p className="text-[#6B6B76] mt-1">Real-time CRE distress analytics & Bitcoin lending</p>
            </div>
            <div className="flex items-center gap-3 mt-4 md:mt-0">
              <div className="bg-white border border-gray-200 rounded-lg px-4 py-2">
                <div className="text-xs text-[#6B6B76]">BTC/USD</div>
                <div className="text-lg font-bold text-[#006AFF]">${btcPrice.toLocaleString()}</div>
              </div>
              <Button className="bg-[#006AFF] hover:bg-[#0055CC] text-white">
                <Wallet className="h-4 w-4 mr-2" />
                Connect Wallet
              </Button>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex gap-2 mb-6 border-b border-gray-200 pb-4">
            {[
              { id: "marketplace", label: "Marketplace", icon: Building2 },
              { id: "lender", label: "Lender Dashboard", icon: BarChart3 },
              { id: "bitcoin", label: "Bitcoin Lending", icon: Bitcoin },
              { id: "analytics", label: "Analytics", icon: TrendingUp }
            ].map((nav) => (
              <button
                key={nav.id}
                onClick={() => setView(nav.id)}
                className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  view === nav.id 
                    ? 'bg-[#006AFF] text-white' 
                    : 'bg-white text-[#6B6B76] hover:bg-gray-100 border border-gray-200'
                }`}
                data-testid={`nav-${nav.id}`}
              >
                <nav.icon className="h-4 w-4 mr-2" />
                {nav.label}
              </button>
            ))}
          </div>

          {/* MARKETPLACE VIEW */}
          {view === "marketplace" && (
            <div>
              {/* Data Source Indicator */}
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-sm text-[#6B6B76]">
                    <Database className="h-4 w-4" />
                    <span>
                      {scraperStatus?.database?.brookfield_properties || 0} Brookfield Properties
                    </span>
                    {scraperStatus?.scraper?.last_scrape && (
                      <span className="text-xs text-[#9B9BA5]">
                        · Last updated: {new Date(scraperStatus.scraper.last_scrape.timestamp).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
                <Button 
                  onClick={runScraper}
                  variant="outline"
                  size="sm"
                  disabled={refreshing}
                  className="text-[#006AFF] border-[#006AFF] hover:bg-[#006AFF] hover:text-white"
                  data-testid="refresh-data-btn"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Refreshing...' : 'Refresh Data'}
                </Button>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                {[
                  { label: "Active Listings", value: properties.length, sub: "Distressed CRE", icon: Building2 },
                  { label: "Total Exposure", value: fmt(LOAN_TOTAL), sub: "Across portfolio", icon: DollarSign },
                  { label: "Avg DSCR", value: AVG_DSCR, sub: "Below 1.0x threshold", icon: TrendingDown },
                  { label: "BTC Lending Pool", value: fmtBtc(120000000), sub: "$120M equivalent", icon: Bitcoin },
                  { label: "Distressed", value: `${scraperStatus?.database?.distressed_percentage || 0}%`, sub: "Of total properties", icon: AlertTriangle },
                ].map((stat, i) => (
                  <Card key={i} className="bg-white border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <stat.icon className="h-5 w-5 text-[#006AFF]" />
                    </div>
                    <div className="text-2xl font-bold text-[#2A2A33]">{stat.value}</div>
                    <div className="text-xs text-[#6B6B76] mt-1">{stat.label}</div>
                    <div className="text-xs text-[#9B9BA5]">{stat.sub}</div>
                  </Card>
                ))}
              </div>

              {/* Filters */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="text-sm text-[#6B6B76]">Type:</span>
                {["All", "Office", "Retail", "Multifamily", "Industrial", "Mixed-use"].map((t) => (
                  <button
                    key={t}
                    onClick={() => setFilterType(t)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      filterType === t 
                        ? 'bg-[#006AFF] text-white' 
                        : 'bg-white text-[#6B6B76] border border-gray-200 hover:border-[#006AFF]'
                    }`}
                    data-testid={`filter-${t.toLowerCase()}`}
                  >
                    {t}
                  </button>
                ))}
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-sm text-[#6B6B76]">Sort:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="bg-white border border-gray-200 text-[#2A2A33] rounded-lg px-3 py-1.5 text-sm focus:border-[#006AFF] outline-none"
                  >
                    <option value="risk_score">Risk Score</option>
                    <option value="loan_amount">Loan Size</option>
                    <option value="discount">Discount</option>
                    <option value="dscr">DSCR</option>
                  </select>
                </div>
              </div>

              {/* Loading State */}
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <RefreshCw className="h-12 w-12 text-[#006AFF] animate-spin mx-auto mb-4" />
                    <p className="text-[#6B6B76]">Loading properties...</p>
                  </div>
                </div>
              ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
                  <Database className="h-16 w-16 text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold text-[#2A2A33] mb-2">No Properties Found</h3>
                  <p className="text-[#6B6B76] mb-6">Run the scraper to populate with Brookfield property data</p>
                  <Button 
                    onClick={runScraper}
                    disabled={refreshing}
                    className="bg-[#006AFF] hover:bg-[#0055CC] text-white"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                    {refreshing ? 'Scraping...' : 'Run Brookfield Scraper'}
                  </Button>
                </div>
              ) : (
                /* Property Cards Grid */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filtered.map((p) => {
                    const TypeIcon = TYPE_ICON[p.type] || TYPE_ICON[p.property_type] || Building2;
                    const riskScore = p.riskScore || p.risk_score || 50;
                    const loanBalance = p.loanBalance || p.loan_amount || 0;
                    const status = p.status || 'Active';
                    const credIQScore = p.credIQScore || p.cred_iq_score || 'C';
                    const discount = p.discount || 15;
                    const dscr = p.dscr || 0.85;
                    const occupancy = p.occupancy || p.occupancy_rate || 70;
                    const btcLendingRate = p.btcLendingRate || p.btc_lending_rate || 8.5;
                    const assumableBalance = p.assumableBalance || p.assumable_balance || Math.round(loanBalance * 0.7);
                    const propertyType = p.type || capitalizeFirst(p.property_type);
                    
                    return (
                      <Card
                        key={p.id}
                        onClick={() => setSelected(p)}
                        className={`bg-white border-gray-200 overflow-hidden cursor-pointer hover:shadow-lg hover:border-[#006AFF] transition-all ${
                          p.watchlist || p.is_distressed ? 'border-l-4 border-l-red-500' : ''
                        }`}
                        data-testid={`property-card-${p.id}`}
                      >
                        <div className="p-5">
                          <div className="flex justify-between items-start mb-3">
                            <div className="bg-[#E8F4FD] p-2 rounded-lg">
                              <TypeIcon className="h-6 w-6 text-[#006AFF]" />
                            </div>
                            <div className="flex gap-2">
                              <span className={`text-xs px-2 py-1 rounded-full ${STATUS_BADGE[status] || 'bg-gray-100 text-gray-700'}`}>
                                {status}
                              </span>
                              <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-[#6B6B76]">
                                {credIQScore}
                              </span>
                            </div>
                          </div>
                          
                          <div className="text-sm text-green-600 font-medium mb-1">{discount}% below par</div>
                          <div className="text-xs text-[#6B6B76] mb-1">{p.id} · {propertyType}</div>
                          <h3 className="text-lg font-bold text-[#2A2A33] mb-1">{p.name}</h3>
                          <div className="text-sm text-[#6B6B76] mb-4 flex items-start">
                            <span className="mr-1">📍</span> {p.address}, {p.city}, {p.state}
                          </div>

                          <div className="grid grid-cols-3 gap-2 mb-4">
                            {[
                              { label: "Balance", value: fmt(loanBalance) },
                              { label: "DSCR", value: dscr.toFixed(2), alert: dscr < 1 },
                              { label: "Occupancy", value: `${occupancy}%`, alert: occupancy < 60 }
                            ].map((s, si) => (
                              <div key={si} className="bg-[#F7F7F7] rounded-lg p-2 text-center">
                                <div className="text-xs text-[#6B6B76]">{s.label}</div>
                                <div className={`text-sm font-semibold ${s.alert ? 'text-red-600' : 'text-[#2A2A33]'}`}>{s.value}</div>
                              </div>
                            ))}
                          </div>

                          {/* Risk Bar */}
                          <div className="mb-3">
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div className={`h-full ${RISK_COLOR(riskScore)}`} style={{ width: `${riskScore}%` }} />
                            </div>
                            <div className="flex justify-between text-xs mt-1">
                              <span className="text-[#6B6B76]">Risk Score</span>
                              <span className={`font-semibold ${RISK_TEXT(riskScore)}`}>{riskScore}/100</span>
                            </div>
                          </div>
                        </div>

                        <div className="bg-[#F7F7F7] border-t border-gray-200 p-4">
                          <div className="flex justify-between mb-3">
                            <div>
                              <div className="text-xs text-[#6B6B76]">BTC Bridge Rate</div>
                              <div className="text-lg font-bold text-[#006AFF]">{btcLendingRate}% APR</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-[#6B6B76]">Assumable</div>
                              <div className="text-lg font-bold text-green-600">{fmt(assumableBalance)}</div>
                              <div className="text-xs text-[#6B6B76]">{fmtBtc(assumableBalance)}</div>
                            </div>
                          </div>
                          <div className="flex gap-2 mb-2">
                            <Button 
                              onClick={(e) => { e.stopPropagation(); setSelected(p); }}
                              className="flex-1 bg-[#006AFF] hover:bg-[#0055CC] text-white text-sm"
                            >
                              View Deal
                            </Button>
                            <Button 
                              onClick={(e) => e.stopPropagation()}
                              variant="outline"
                              className="flex-1 border-gray-300 text-[#2A2A33] hover:bg-gray-100 text-sm"
                            >
                              Make Offer
                            </Button>
                          </div>
                          <Button 
                            onClick={(e) => { e.stopPropagation(); setDeRiskProperty(p); setShowDeRiskPanel(true); }}
                            className="w-full bg-gradient-to-r from-[#006AFF] to-[#0055CC] hover:from-[#0055CC] hover:to-[#004499] text-white text-sm"
                            data-testid={`derisk-btn-${p.id}`}
                          >
                            <Shield className="h-4 w-4 mr-2" />
                            AI De-Risk Analysis
                          </Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* LENDER VIEW */}
          {view === "lender" && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-[#2A2A33]">Lender & Banker De-Risk Dashboard</h2>
                <p className="text-[#6B6B76]">Powered by Cred-IQ Surveillance Data · Real-time CRE Risk Analytics</p>
              </div>

              <div className="flex gap-2 mb-6">
                {["portfolio", "surveillance", "exit"].map((t) => (
                  <button
                    key={t}
                    onClick={() => setLenderTab(t)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                      lenderTab === t 
                        ? 'bg-[#006AFF] text-white' 
                        : 'bg-white text-[#6B6B76] border border-gray-200 hover:border-[#006AFF]'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              {lenderTab === "portfolio" && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    {[
                      { label: "Total Exposure", value: fmt(LOAN_TOTAL), alert: true, sub: `${properties.length} properties` },
                      { label: "Special Servicing", value: `${properties.filter(p => p.status?.toLowerCase().includes('special') || p.status?.toLowerCase().includes('servicing')).length} of ${properties.length}`, alert: true, sub: "loans in default" },
                      { label: "Weighted DSCR", value: `${AVG_DSCR}x`, alert: parseFloat(AVG_DSCR) < 1, sub: "Portfolio average" },
                      { label: "Distressed Rate", value: `${scraperStatus?.database?.distressed_percentage || 0}%`, alert: true, sub: "of total exposure" },
                    ].map((k, i) => (
                      <Card key={i} className={`bg-white p-5 shadow-sm ${k.alert ? 'border-l-4 border-l-red-500' : 'border-gray-200'}`}>
                        <div className={`text-3xl font-bold ${k.alert ? 'text-red-600' : 'text-green-600'}`}>{k.value}</div>
                        <div className="text-sm text-[#6B6B76] mt-1">{k.label}</div>
                        <div className="text-xs text-[#9B9BA5]">{k.sub}</div>
                      </Card>
                    ))}
                  </div>

                  <Card className="bg-white border-gray-200 overflow-hidden shadow-sm">
                    <div className="px-5 py-4 border-b border-gray-200 flex justify-between items-center">
                      <span className="font-semibold text-[#2A2A33]">Cred-IQ Surveillance Loans</span>
                      <span className="text-xs text-green-600 flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                        LIVE
                      </span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-[#F7F7F7]">
                          <tr>
                            {["Loan ID", "Property", "Type", "Balance", "DSCR", "LTV", "Status", "Cred-IQ", "BTC Rate", "Action"].map((h) => (
                              <th key={h} className="px-4 py-3 text-left text-[#6B6B76] font-medium">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {properties.slice(0, 20).map((p, i) => {
                            const dscr = p.dscr || 0.85;
                            const ltv = p.ltv_ratio || p.currentLTV || 75;
                            const loanBalance = p.loan_amount || p.loanBalance || 0;
                            const btcRate = p.btc_lending_rate || p.btcLendingRate || 8.5;
                            const credIQ = p.cred_iq_score || p.credIQScore || 'C';
                            const propType = p.property_type || p.type || 'Office';
                            
                            return (
                              <tr key={i} className="border-b border-gray-100 hover:bg-[#F7F7F7]">
                                <td className="px-4 py-3 text-[#6B6B76]">{p.id}</td>
                                <td className="px-4 py-3 text-[#2A2A33] font-medium">{p.name?.substring(0, 25)}{p.name?.length > 25 ? '...' : ''}</td>
                                <td className="px-4 py-3 text-[#6B6B76]">{capitalizeFirst(propType)}</td>
                                <td className="px-4 py-3 text-[#2A2A33]">{fmt(loanBalance)}</td>
                                <td className={`px-4 py-3 ${dscr < 1 ? 'text-red-600' : 'text-[#2A2A33]'}`}>{dscr.toFixed(2)}</td>
                                <td className={`px-4 py-3 ${ltv > 90 ? 'text-red-600' : 'text-[#2A2A33]'}`}>{ltv}%</td>
                                <td className="px-4 py-3">
                                  <span className={`text-xs px-2 py-1 rounded-full ${STATUS_BADGE[p.status] || 'bg-gray-100 text-gray-700'}`}>{p.status}</span>
                                </td>
                                <td className="px-4 py-3 text-[#006AFF] font-medium">{credIQ}</td>
                                <td className="px-4 py-3 text-green-600">{btcRate}%</td>
                                <td className="px-4 py-3">
                                  <Button
                                    onClick={() => { setDeRiskProperty(p); setShowDeRiskPanel(true); }}
                                    size="sm"
                                    className="bg-gradient-to-r from-[#006AFF] to-[#0055CC] hover:from-[#0055CC] hover:to-[#004499] text-white"
                                  >
                                    <Shield className="h-3 w-3 mr-1" />
                                    AI De-Risk
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </>
              )}

              {lenderTab === "surveillance" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="bg-white border-gray-200 p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-[#2A2A33] mb-4">Cred-IQ Grade Distribution</h3>
                    <div className="flex gap-4 items-end h-48">
                      {["D-", "D", "D+", "C-", "C", "C+"].map((g, i) => {
                        const count = properties.filter(p => (p.cred_iq_score || p.credIQScore) === g).length;
                        const colors = ["bg-red-500", "bg-red-400", "bg-orange-500", "bg-orange-400", "bg-yellow-500", "bg-yellow-400"];
                        return (
                          <div key={g} className="flex-1 flex flex-col items-center">
                            <div className={`w-full ${colors[i]} rounded-t`} style={{ height: Math.max(10, count * 15) }} />
                            <div className="text-xs text-[#6B6B76] mt-2">{g}</div>
                            <div className="text-sm font-semibold text-[#2A2A33]">{count}</div>
                          </div>
                        );
                      })}
                    </div>
                  </Card>

                  <Card className="bg-white border-gray-200 p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-[#2A2A33] mb-4">Maturity Wall</h3>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {[...properties].filter(p => p.maturity_date || p.maturityDate).sort((a, b) => new Date(a.maturity_date || a.maturityDate) - new Date(b.maturity_date || b.maturityDate)).slice(0, 10).map((p) => {
                        const maturityDate = p.maturity_date || p.maturityDate;
                        const loanBalance = p.loan_amount || p.loanBalance || 0;
                        return (
                          <div key={p.id} className="flex justify-between items-center py-2 border-b border-gray-100">
                            <div>
                              <div className="text-xs text-[#6B6B76]">{p.id}</div>
                              <div className="text-sm text-[#2A2A33]">{p.name?.substring(0, 22)}{p.name?.length > 22 ? '...' : ''}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-semibold text-[#2A2A33]">{fmt(loanBalance)}</div>
                              <div className={`text-xs ${new Date(maturityDate) < new Date() ? 'text-red-600' : 'text-[#6B6B76]'}`}>
                                {maturityDate}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                </div>
              )}

              {lenderTab === "exit" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { title: "BTC Collateral Bridge", desc: "Replace maturing CRE debt with Bitcoin-collateralized bridge loans.", rate: "7.2–10.2% APR", icon: Bitcoin, action: "Structure Bridge" },
                    { title: "Note Sale Marketplace", desc: "List distressed notes to qualified buyers on AssumeRate platform.", rate: "60–85¢ on $1", icon: DollarSign, action: "List Note" },
                    { title: "DIP Financing", desc: "Provide debtor-in-possession financing with BTC overcollateralization.", rate: "Prime + 450bps", icon: AlertTriangle, action: "Structure DIP" },
                  ].map((s, i) => (
                    <Card key={i} className="bg-white border-gray-200 p-6 shadow-sm">
                      <div className="bg-[#E8F4FD] w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                        <s.icon className="h-6 w-6 text-[#006AFF]" />
                      </div>
                      <h3 className="text-lg font-bold text-[#2A2A33] mb-2">{s.title}</h3>
                      <p className="text-sm text-[#6B6B76] mb-4">{s.desc}</p>
                      <div className="text-xs text-[#6B6B76] mb-1">Typical Rate</div>
                      <div className="text-xl font-bold text-[#006AFF] mb-4">{s.rate}</div>
                      <Button className="w-full bg-[#006AFF] hover:bg-[#0055CC] text-white">{s.action}</Button>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* BITCOIN LENDING VIEW */}
          {view === "bitcoin" && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-[#2A2A33]">Bitcoin CRE Lending Engine</h2>
                <p className="text-[#6B6B76]">Use Bitcoin as collateral to access liquidity for distressed commercial real estate</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <Card className="bg-white border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-[#006AFF] mb-6">Loan Calculator</h3>
                  {[
                    { label: "Loan Amount (USD)", key: "amount", min: 500000, max: 100000000, step: 500000, fmt: (v) => `$${(v / 1e6).toFixed(1)}M` },
                    { label: "Term (Months)", key: "term", min: 6, max: 60, step: 6, fmt: (v) => `${v} mo` },
                    { label: "Interest Rate (%)", key: "rate", min: 5, max: 15, step: 0.1, fmt: (v) => `${Number(v).toFixed(1)}%` },
                  ].map((s, i) => (
                    <div key={i} className="mb-5">
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-[#6B6B76]">{s.label}</span>
                        <span className="text-sm font-semibold text-[#2A2A33]">{s.fmt(btcCalc[s.key])}</span>
                      </div>
                      <input
                        type="range"
                        min={s.min}
                        max={s.max}
                        step={s.step}
                        value={btcCalc[s.key]}
                        onChange={(e) => setBtcCalc({ ...btcCalc, [s.key]: parseFloat(e.target.value) })}
                        className="w-full accent-[#006AFF]"
                      />
                    </div>
                  ))}
                  <div className="bg-[#F7F7F7] rounded-lg p-4 mt-4">
                    {[
                      { l: "Monthly Payment", v: `$${parseInt(btcMonthly).toLocaleString()}` },
                      { l: "Total Repayment", v: `$${parseInt(btcTotal).toLocaleString()}` },
                      { l: "BTC Collateral Req.", v: fmtBtc(btcCalc.amount * 1.5), highlight: true },
                    ].map((r, ri) => (
                      <div key={ri} className={`flex justify-between py-2 ${ri < 2 ? 'border-b border-gray-200' : ''}`}>
                        <span className="text-[#6B6B76]">{r.l}</span>
                        <span className={`font-semibold ${r.highlight ? 'text-[#006AFF]' : 'text-[#2A2A33]'}`}>{r.v}</span>
                      </div>
                    ))}
                  </div>
                </Card>

                <Card className="bg-white border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-[#2A2A33] mb-6">How It Works</h3>
                  {[
                    { n: "01", t: "Pledge BTC Collateral", d: "Lock Bitcoin in multi-sig smart contract at 150% LTV." },
                    { n: "02", t: "Receive USD Bridge Loan", d: "Get USD liquidity at 7.2–10.2% APR based on risk score." },
                    { n: "03", t: "Acquire Distressed Asset", d: "Use proceeds to assume loan or inject equity into property." },
                    { n: "04", t: "Stabilize & Refinance", d: "Increase NOI, refinance into conventional debt, release BTC." },
                  ].map((s, i) => (
                    <div key={i} className="flex gap-4 mb-4">
                      <div className="w-10 h-10 bg-[#E8F4FD] rounded-lg flex items-center justify-center text-[#006AFF] font-bold text-sm flex-shrink-0">
                        {s.n}
                      </div>
                      <div>
                        <div className="font-semibold text-[#2A2A33]">{s.t}</div>
                        <div className="text-sm text-[#6B6B76]">{s.d}</div>
                      </div>
                    </div>
                  ))}
                </Card>
              </div>

              <Card className="bg-white border-gray-200 p-6 shadow-sm mb-8">
                <h3 className="text-lg font-semibold text-[#2A2A33] mb-4">Active Lending Pools</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { name: "AssumeRate Bridge Fund I", aum: 45000000, min: 2000000, maxLTV: 65, rate: "7.5–9.0%", open: true },
                    { name: "Bitcoin CRE Recovery LP", aum: 78000000, min: 5000000, maxLTV: 70, rate: "8.0–10.5%", open: true },
                    { name: "Digital Asset Debt Fund III", aum: 120000000, min: 10000000, maxLTV: 60, rate: "7.2–8.8%", open: false },
                  ].map((f, fi) => (
                    <div key={fi} className={`bg-[#F7F7F7] rounded-lg p-4 border ${f.open ? 'border-green-200' : 'border-gray-200'}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div className="font-semibold text-[#2A2A33]">{f.name}</div>
                        <span className={`text-xs px-2 py-1 rounded-full ${f.open ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {f.open ? "Open" : "Closed"}
                        </span>
                      </div>
                      <div className="text-sm text-[#6B6B76] mb-3">AUM: {fmt(f.aum)} · Min: {fmt(f.min)}</div>
                      <div className="flex justify-between text-sm">
                        <span>Rate: <span className="text-[#006AFF] font-medium">{f.rate}</span></span>
                        <span>Max LTV: <span className="text-green-600 font-medium">{f.maxLTV}%</span></span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="bg-gradient-to-r from-[#E8F4FD] to-[#D4E8FF] border-[#006AFF]/20 p-6">
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <div className="bg-[#006AFF] p-4 rounded-xl">
                    <Bitcoin className="h-10 w-10 text-white" />
                  </div>
                  <div className="flex-1 text-center md:text-left">
                    <h3 className="text-xl font-bold text-[#2A2A33] mb-1">Become a Lender</h3>
                    <p className="text-[#6B6B76]">Deploy Bitcoin capital into secured CRE bridge loans. 8–12% yields backed by first-lien collateral.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {["First-Lien Security", "BTC Overcollateral", "Monthly Cash Flow"].map((f) => (
                      <span key={f} className="text-xs px-3 py-1 bg-white rounded-full text-[#6B6B76] border border-gray-200">✓ {f}</span>
                    ))}
                  </div>
                  <Button className="bg-[#006AFF] hover:bg-[#0055CC] text-white px-6">Apply as Lender</Button>
                </div>
              </Card>
            </div>
          )}

          {/* ANALYTICS VIEW */}
          {view === "analytics" && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-[#2A2A33]">Market Analytics</h2>
                <p className="text-[#6B6B76]">CRE distress trends & Bitcoin lending market intelligence</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <Card className="md:col-span-2 bg-white border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-[#2A2A33] mb-6">Avg DSCR by Property Type (Cred-IQ Data)</h3>
                  <div className="flex gap-6 items-end h-52">
                    {["office", "retail", "multifamily", "industrial", "mixed-use"].map((type, ti) => {
                      const props = properties.filter(p => (p.property_type || p.type || '').toLowerCase() === type);
                      const avg = props.length ? props.reduce((a, p) => a + (p.dscr || 0.8), 0) / props.length : 0;
                      const h = avg * 140;
                      const colors = ["bg-red-500", "bg-orange-500", "bg-blue-500", "bg-green-500", "bg-purple-500"];
                      return (
                        <div key={type} className="flex-1 flex flex-col items-center">
                          <div className={`w-full ${colors[ti]} rounded-t`} style={{ height: Math.max(10, h) }} />
                          <div className="text-lg font-bold text-[#2A2A33] mt-2">{avg.toFixed(2)}</div>
                          <div className="text-xs text-[#6B6B76]">{capitalizeFirst(type)}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-t border-dashed border-red-300 mt-4 relative">
                    <span className="absolute -top-2 left-0 text-xs text-red-500 bg-white px-2">1.0x threshold</span>
                  </div>
                </Card>

                <Card className="bg-white border-gray-200 p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-[#2A2A33] mb-4">Risk Heat Map</h3>
                  <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                    {properties.slice(0, 12).map((p) => {
                      const riskScore = p.risk_score || p.riskScore || 50;
                      return (
                        <div
                          key={p.id}
                          className={`p-3 rounded-lg text-center border ${
                            riskScore >= 80 ? 'bg-red-50 border-red-200' : 
                            riskScore >= 60 ? 'bg-orange-50 border-orange-200' : 'bg-yellow-50 border-yellow-200'
                          }`}
                        >
                          <div className="text-xs text-[#6B6B76] truncate">{p.name?.split(" ").slice(0, 2).join(" ")}</div>
                          <div className={`text-xl font-bold ${RISK_TEXT(riskScore)}`}>{riskScore}</div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { t: "Total Portfolio Value", v: fmt(properties.reduce((a, p) => a + (p.property_value || 0), 0)), s: `${properties.length} properties`, sub: "Brookfield sourced", c: "blue" },
                  { t: "Distressed Properties", v: properties.filter(p => p.is_distressed).length.toString(), s: `${scraperStatus?.database?.distressed_percentage || 0}% of portfolio`, sub: "Requires attention", c: "red" },
                  { t: "BTC vs CMBS Yield", v: "+340bps", s: "BTC-backed over CMBS", sub: "Spread compression YTD", c: "blue" },
                ].map((s, i) => (
                  <Card key={i} className={`bg-white p-6 shadow-sm border-l-4 ${
                    s.c === 'red' ? 'border-l-red-500' : s.c === 'orange' ? 'border-l-orange-500' : 'border-l-[#006AFF]'
                  }`}>
                    <div className="text-sm text-[#6B6B76] mb-2">{s.t}</div>
                    <div className={`text-4xl font-bold ${
                      s.c === 'red' ? 'text-red-600' : s.c === 'orange' ? 'text-orange-600' : 'text-[#006AFF]'
                    }`}>{s.v}</div>
                    <div className="text-sm text-[#2A2A33] mt-2">{s.s}</div>
                    <div className="text-xs text-[#6B6B76]">{s.sub}</div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* PROPERTY DETAIL MODAL */}
      {selected && (
        <div 
          onClick={() => setSelected(null)} 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          data-testid="property-modal"
        >
          <Card 
            onClick={(e) => e.stopPropagation()} 
            className="bg-white border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-auto shadow-xl"
          >
            <div className="p-6">
              {(() => {
                const loanBalance = selected.loan_amount || selected.loanBalance || 0;
                const currentLTV = selected.ltv_ratio || selected.currentLTV || 75;
                const dscr = selected.dscr || 0.85;
                const occupancy = selected.occupancy_rate || selected.occupancy || 70;
                const noi = selected.noi || 0;
                const sqft = selected.size_sqft || selected.sqft || 0;
                const maturityDate = selected.maturity_date || selected.maturityDate || '2025-12-31';
                const discount = selected.discount || 20;
                const credIQScore = selected.cred_iq_score || selected.credIQScore || 'C';
                const btcLendingRate = selected.btc_lending_rate || selected.btcLendingRate || 8.5;
                const assumableBalance = selected.assumable_balance || selected.assumableBalance || Math.round(loanBalance * 0.7);
                const propType = selected.property_type || selected.type || 'Commercial';
                
                return (
                  <>
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <div className="text-sm text-[#6B6B76] mb-1">{selected.id} · {capitalizeFirst(propType)}</div>
                        <h2 className="text-xl font-bold text-[#2A2A33]">{selected.name}</h2>
                        <div className="text-sm text-[#6B6B76] mt-1">📍 {selected.address}, {selected.city}, {selected.state}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm px-3 py-1 rounded-full bg-[#E8F4FD] text-[#006AFF]">
                          Cred-IQ {credIQScore}
                        </span>
                        <button 
                          onClick={() => setSelected(null)} 
                          className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-[#6B6B76]"
                        >
                          ✕
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-3 mb-6">
                      {[
                        { l: "Loan Balance", v: fmt(loanBalance) },
                        { l: "Current LTV", v: `${currentLTV}%`, alert: currentLTV > 85 },
                        { l: "DSCR", v: dscr.toFixed(2), alert: dscr < 1 },
                        { l: "Occupancy", v: `${occupancy}%`, alert: occupancy < 60 },
                        { l: "NOI", v: fmt(noi) },
                        { l: "Sq Footage", v: `${(sqft / 1000).toFixed(0)}K SF` },
                        { l: "Maturity Date", v: maturityDate, alert: new Date(maturityDate) < new Date() },
                        { l: "Discount to Par", v: `${discount}%` },
                      ].map((s, si) => (
                        <div key={si} className="bg-[#F7F7F7] rounded-lg p-3">
                          <div className="text-xs text-[#6B6B76]">{s.l}</div>
                          <div className={`text-sm font-semibold ${s.alert ? 'text-red-600' : 'text-[#2A2A33]'}`}>{s.v}</div>
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="font-semibold text-green-700 mb-3">Buyer Opportunity</h4>
                        <div className="text-sm text-[#6B6B76] space-y-1">
                          <p>📉 {discount}% below loan balance</p>
                          <p>🏦 Assumable balance: {fmt(assumableBalance)}</p>
                          <p>₿ BTC bridge rate: {btcLendingRate}% APR</p>
                          <p>📋 Status: {selected.status}</p>
                        </div>
                        <Button className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white">
                          Submit Letter of Intent
                        </Button>
                      </div>

                      <div className="bg-[#E8F4FD] border border-[#006AFF]/20 rounded-lg p-4">
                        <h4 className="font-semibold text-[#006AFF] mb-3">Lender De-Risk Options</h4>
                        <div className="text-sm text-[#6B6B76] space-y-1">
                          <p>⚡ BTC Bridge: Replace at {btcLendingRate}%</p>
                          <p>📑 Note Sale: Est. {100 - discount}¢ on $1</p>
                          <p>🔄 Loan Mod: Extend + rate reset</p>
                          <p>💡 Equity Conversion: Debt-to-equity swap</p>
                        </div>
                        <Button 
                          onClick={() => { setDeRiskProperty(selected); setShowDeRiskPanel(true); setSelected(null); }}
                          className="w-full mt-4 bg-[#006AFF] hover:bg-[#0055CC] text-white"
                        >
                          <Shield className="h-4 w-4 mr-2" />
                          AI De-Risk Analysis
                        </Button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </Card>
        </div>
      )}

      {/* De-Risk Analysis Panel */}
      <DeRiskAnalysisPanel 
        property={deRiskProperty}
        isOpen={showDeRiskPanel}
        onClose={() => { setShowDeRiskPanel(false); setDeRiskProperty(null); }}
      />

      <Footer />
    </div>
  );
};

export default Dashboard;
